'use client'
import React, { useState } from 'react';
import { Button } from './Buttons';
import {
  Popover,
  PopoverButton,
  PopoverBackdrop,
  PopoverPanel,
} from '@headlessui/react'

import clsx from 'clsx'

import { Container } from './Container'
import logo from '../../assets/images/logo.jpeg';
import { NavLink } from './NavLink'
import { handleAnchorClick } from '../../utils/scrollUtils';

function MobileNavLink({ href, children, onClick, type }) {
  const handleClick = (e) => {
    // Close the mobile menu first
    if (onClick) {
      onClick();
    }
    
    // Only handle hash links after menu is closed
    if (href && href.startsWith('#')) {
      e.preventDefault();
      
      // Log the click for debugging
      console.log(`Mobile clicking link to: ${href}`);
      
      // Get the element ID
      const id = href.substring(1);
      
      // Add a small delay to ensure menu is closed before scrolling
      setTimeout(() => {
        const element = document.getElementById(id);
        
        // Log if element exists
        console.log(`Mobile element found: ${!!element}`);
        
        if (element) {
          // Scroll to the element with offset
          const headerOffset = 80;
          
          // Try this different approach to scrolling
          element.scrollIntoView({ behavior: 'smooth' });
          
          // Apply the offset after scrolling
          setTimeout(() => {
            window.scrollBy({
              top: -headerOffset,
              behavior: 'smooth'
            });
          }, 100);
        } else {
          console.error(`Element with id '${id}' not found`);
          
          // Fall back to trying to find the element by section
          const sections = document.querySelectorAll('section');
          let found = false;
          
          sections.forEach(section => {
            if (section.id === id) {
              found = true;
              console.log(`Found section with id: ${id}`);
              section.scrollIntoView({ behavior: 'smooth' });
              
              // Apply the offset after scrolling
              setTimeout(() => {
                const headerOffset = 80;
                window.scrollBy({
                  top: -headerOffset,
                  behavior: 'smooth'
                });
              }, 100);
            }
          });
          
          if (!found) {
            console.error(`Could not find any section with id: ${id}`);
          }
        }
      }, 150);
    }
  };

  // Use the provided type to determine styling
  const getClassNames = () => {
    if (type === 'signin') {
      return 'mobile-sign-in';
    } else if (type === 'nav') {
      return 'mobile-nav-item';
    }
    return '';
  };

  return (
    <PopoverButton 
      as="a" 
      href={href} 
      onClick={handleClick}
      className={`mobile-menu-link block w-full my-1 ${getClassNames()}`}
    >
      <div className="flex items-center">
        {type === 'signin' && (
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-4 w-4 mr-2" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" 
            />
          </svg>
        )}
        {children}
      </div>
    </PopoverButton>
  );
}

function MobileNavIcon({ open }) {
  return (
    <div className="flex items-center justify-center w-9 h-9 rounded-full premium-menu-toggle">
      <svg
        aria-hidden="true"
        className="h-4 w-4 overflow-visible stroke-slate-200"
        fill="none"
        strokeWidth={1.8}
        strokeLinecap="round"
      >
        <path
          d="M0 1H14M0 7H14M0 13H14"
          className={clsx(
            'origin-center transition duration-200',
            open && 'scale-90 opacity-0',
          )}
        />
        <path
          d="M2 2L12 12M12 2L2 12"
          className={clsx(
            'origin-center transition duration-200',
            !open && 'scale-90 opacity-0',
          )}
        />
      </svg>
    </div>
  )
}

function MobileNavigation() {
  const [isOpen, setIsOpen] = useState(false);
  
  // Function to close the menu
  const closeMenu = () => {
    console.log('Closing mobile menu');
    setIsOpen(false);
  };

  return (
    <Popover 
      open={isOpen} 
      onOpenChange={setIsOpen}
    >
      <PopoverButton
        className="relative z-10 flex items-center justify-center ui-not-focus-visible:outline-none"
        aria-label="Toggle Navigation"
        onClick={() => setIsOpen(!isOpen)}
      >
        {({ open }) => <MobileNavIcon open={open} />}
      </PopoverButton>
      <PopoverBackdrop
        transition
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm duration-150 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in"
        onClick={() => {
          console.log('Backdrop clicked, closing menu');
          closeMenu();
        }}
      />
      <PopoverPanel
        transition
        className="absolute inset-x-4 sm:inset-x-8 top-full mt-4 flex origin-top flex-col rounded-xl text-base tracking-tight text-white shadow-xl premium-mobile-menu data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-150 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="p-4 space-y-2">
          <div className="px-1 mb-3 mobile-menu-header">
            <div className="flex items-center justify-between mb-4">
              <a href="/" className="flex items-center gap-x-2">
                <img alt="Audit-AI" src={logo} className="h-7 w-auto" />
                <span className="text-lg font-bold text-white">AUDIT-AI</span>
              </a>
              <PopoverButton
                className="flex h-8 w-8 items-center justify-center rounded-full menu-close-button"
                aria-label="Close menu"
                onClick={closeMenu}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </PopoverButton>
            </div>
            <div className="h-px bg-gradient-to-r from-indigo-500/0 via-indigo-500/40 to-indigo-500/0 mb-4" />
          </div>
          
          <h3 className="mobile-menu-category pl-2 mb-1">Navigation</h3>
          <MobileNavLink href="#features" onClick={closeMenu} type="nav">Features</MobileNavLink>
          <MobileNavLink href="#secondaryFeatures" onClick={closeMenu} type="nav">Solutions</MobileNavLink>
          <MobileNavLink href="#integrations" onClick={closeMenu} type="nav">Technology</MobileNavLink>
          <MobileNavLink href="/blog" onClick={closeMenu} type="nav">Blog</MobileNavLink>
          <MobileNavLink href="#faqs" onClick={closeMenu} type="nav">FAQ</MobileNavLink>
          
          <div className="my-3 h-px bg-gradient-to-r from-indigo-500/0 via-indigo-500/20 to-indigo-500/0" />
          
          <h3 className="mobile-menu-category pl-2 mb-1">Account</h3>
          <MobileNavLink href="/login" onClick={closeMenu} type="signin">Sign in</MobileNavLink>
          <div className="pt-2">
            <Button 
              href="/register" 
              color="indigo" 
              className="w-full py-2 justify-center bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-md hover:shadow-lg transition-all text-sm"
              onClick={() => {
                console.log('Get started button clicked, closing menu');
                closeMenu();
              }}
            >
              <span className="flex items-center justify-center">
                Get started
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-4 w-4 ml-2" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M14 5l7 7m0 0l-7 7m7-7H3" 
                  />
                </svg>
              </span>
            </Button>
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  )
}

export function Header() {
  return (
    <header className="py-6 px-4 sm:py-8 sm:px-6 lg:px-8 sticky top-0 z-50 bg-white/90 backdrop-blur-sm shadow-sm">
      <Container>
        <nav className="relative z-50 flex justify-between items-center">
          <div className="flex items-center">
            <a href="/" aria-label="Home" className="flex items-center gap-x-2 sm:gap-x-3 no-underline">
              <img alt="Audit-AI" src={logo} className="h-8 w-auto sm:h-10" />
              <span className="text-base sm:text-lg md:text-xl font-semibold text-slate-900 whitespace-nowrap">AUDIT-AI</span>
            </a>

            <div className="hidden md:flex md:gap-x-6 md:ml-10">
              <NavLink href="#features">Features</NavLink>
              <NavLink href="#secondaryFeatures">Solutions</NavLink>
              <NavLink href="#integrations">Technology</NavLink>
              <NavLink href="/blog">Blog</NavLink>
              <NavLink href="#faqs">FAQ</NavLink>
            </div>
          </div>
          <div className="flex items-center gap-x-3 sm:gap-x-5 md:gap-x-8">
            <div className="hidden md:block">
              <NavLink href="/login">Sign in</NavLink>
            </div>
            <Button href="/register" color="blue" className="whitespace-nowrap px-3 sm:px-4 py-2 text-sm sm:text-base">
              <span className="flex items-center">
                Get started
              </span>
            </Button>
            <div className="-mr-1 md:hidden">
              <MobileNavigation />
            </div>
          </div>
        </nav>
      </Container>
    </header>
  )
}
